import { CommonModule, NgIf } from '@angular/common'
import { Component, DestroyRef, inject, LOCALE_ID, OnDestroy, OnInit } from '@angular/core'
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators
} from '@angular/forms'
import {
  CUSTOM_DEDUCTIONS,
  CUSTOM_EARNINGS,
  REIMBURSEMENTS
} from '../../../../store/state.constants'
import { ModalsComponent } from '../modals/modals.component'
import { MemoizedSelector, select, Store } from '@ngrx/store'
import { NgbModal } from '@ng-bootstrap/ng-bootstrap'
import { selectSalaryComponents } from '../../../../store/salary-components/salary-components.selectors'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import {
  BehaviorSubject,
  combineLatest,
  filter,
  firstValueFrom,
  map,
  Observable,
  take,
  tap
} from 'rxjs'
import { SalaryComponent } from '../../../../store/salary-components/salary-components.state'
import {
  selectCtcValue,
  selectRevisedCtcValue,
  selectRevisedSalaryData,
  selectRevisedStatutoryCompliances,
  selectSalaryData,
  selectStatutoryCompliances
} from '../../../../store/employee-salary/employee-salary.selectors'
import {
  EmployeeSalary,
  ExtendedSalaryComponent,
  SalaryData,
  SalaryMode,
  StatutoryCompliances
} from '../../../../store/employee-salary/employee-salary.state'
import {
  addSalaryData,
  salaryRevisionActions
} from '../../../../store/employee-salary/employee-salary.actions'
import { ActivatedRoute } from '@angular/router'
import { IndianNumberInputFormatDirective } from '../../../../directives/indian-number-input-format.directive'
import {
  selectEmployeeSalary,
  selectRevisedSalary
} from '../../../../store/employee-salary/employee-salary.reducers'
import { User, UserRole } from '../../../../store/auth/auth.state'
import { selectSelectedUser } from '../../../../store/user/user.reducer'
import { CapitalizePipe } from '../../../../pipes/capitalize.pipe'

@Component({
  selector: 'app-salary-data',
  imports: [
    NgIf,
    FormsModule,
    CommonModule,
    ReactiveFormsModule,
    IndianNumberInputFormatDirective,
    CapitalizePipe
  ],
  providers: [{ provide: LOCALE_ID, useValue: 'en-IN' }],
  templateUrl: './salary-data.component.html',
  styleUrl: './salary-data.component.scss'
})
export class SalaryDataComponent implements OnInit, OnDestroy {
  private store = inject(Store)
  private route = inject(ActivatedRoute)
  private destroyRef = inject(DestroyRef)
  private modalService = inject(NgbModal)
  private fb = inject(FormBuilder)

  currentMode!: SalaryMode
  salaryMode = SalaryMode
  form!: FormGroup

  //---Defaults---//
  ctcValue = 0
  basicPercentage = 50
  hraPercentage = 0
  annualPfCap = 21600

  complianceSettings!: StatutoryCompliances
  earnings!: SalaryComponent[]
  earnings$!: Observable<SalaryComponent[]>
  earningsTabName = CUSTOM_EARNINGS
  deductions!: SalaryComponent[]
  deductions$!: Observable<SalaryComponent[]>
  deductionsTabName = CUSTOM_DEDUCTIONS
  reimbursements!: SalaryComponent[]
  reimbursements$!: Observable<SalaryComponent[]>
  reimbursementsTabName = REIMBURSEMENTS
  OtherComponentsModal = ModalsComponent

  //---State---//
  selectedComponentItem: SalaryComponent | null = null
  employerContribution = 0
  employeeContribution = 0
  isEditing = false
  currentStep = 1
  addCompError: string | null = null
  searchTerm = ''
  private searchTerm$ = new BehaviorSubject<string>('')
  currentCtcValue = 0
  currency = 'INR'
  user!: User

  // --- Filtered Observables for Modals ---
  filteredEarnings$!: Observable<SalaryComponent[]>
  filteredDeductions$!: Observable<SalaryComponent[]>
  filteredReimbursements$!: Observable<SalaryComponent[]>
  gross = this.ctcValue
  esiEmployeeContribution = 0
  esiEmployerContribution = 0
  private readonly SALARY_CALC_CTC_KEY = 'salaryCalculatedCtc'
  // ✅ Track if data has been loaded to prevent double initialization
  private dataLoaded = false
  constructor() {
    // const savedCtc = localStorage.getItem('currentCtc')
    // if (savedCtc) {
    //   this.currentCtcValue = Number(savedCtc)
    // }
    // if (this.ctcValue === 0) {
    //   this.ctcValue = this.currentCtcValue
    // }
    const savedSalaryCtc = localStorage.getItem(this.SALARY_CALC_CTC_KEY)
    if (savedSalaryCtc) {
      this.currentCtcValue = Number(savedSalaryCtc)
    } else {
      const savedCtc = localStorage.getItem('currentCtc')
      if (savedCtc) {
        this.currentCtcValue = Number(savedCtc)
      }
    }
    if (this.ctcValue === 0) {
      this.ctcValue = this.currentCtcValue
    }
  }

  async ngOnInit() {
    this.currentMode = this.route.snapshot.data['mode'] as SalaryMode
    // const savedCtc = localStorage.getItem('currentCtc')
    // if (savedCtc) {
    //   this.currentCtcValue = Number(savedCtc)
    // }
    // if (this.ctcValue === 0) {
    //   this.ctcValue = this.currentCtcValue
    // }

    const savedSalaryCtc = localStorage.getItem(this.SALARY_CALC_CTC_KEY)
    if (savedSalaryCtc) {
      this.currentCtcValue = Number(savedSalaryCtc)
    } else {
      const savedCtc = localStorage.getItem('currentCtc')
      if (savedCtc) {
        this.currentCtcValue = Number(savedCtc)
      }
    }
    if (this.ctcValue === 0) {
      this.ctcValue = this.currentCtcValue
    }

    this.user = await firstValueFrom(
      this.store.pipe(
        select(selectSelectedUser),
        filter((user): user is User => !!user),
        take(1),
        tap(user => {
          this.isContractor(user) ? (this.hraPercentage = 0) : (this.hraPercentage = 40)
        })
      )
    )

    // Get currency from appropriate selector based on mode
    let selector: MemoizedSelector<object, EmployeeSalary>
    this.currentMode === SalaryMode.CREATE
      ? (selector = selectEmployeeSalary)
      : (selector = selectRevisedSalary)
    const salary = await firstValueFrom(this.store.select(selector))
    this.currency = salary.ctc.currency

    this.initializeForm()
    this.initializeComponentState()

    // ✅ FIXED: Load data with priority - revised first, then original
    // This runs AFTER initializeComponentState so compliance and CTC are set
    await this.loadSalaryData()
  }

  // ✅ NEW METHOD: Load data with proper priority
  // private async loadSalaryData(): Promise<void> {
  //   // First check if revised data exists
  //   const revisedData = await firstValueFrom(
  //     this.store.select(selectRevisedSalaryData).pipe(take(1))
  //   )

  //   const hasRevisedData =
  //     revisedData &&
  //     (revisedData.basic > 0 ||
  //       (revisedData.otherComponents && revisedData.otherComponents.length > 0))

  //   if (hasRevisedData) {
  //     // console.log('Loading REVISED data:', revisedData)
  //     this.setSalaryData(this.ensureIntegerAmounts(revisedData))
  //     this.dataLoaded = true
  //     return
  //   }

  //   // If no revised data, load original salaryData
  //   const originalData = await firstValueFrom(this.store.select(selectSalaryData).pipe(take(1)))

  //   if (originalData) {
  //     // console.log('Loading ORIGINAL data:', originalData)
  //     this.setSalaryData(this.ensureIntegerAmounts(originalData))
  //     this.dataLoaded = true
  //   }
  // }

  private async loadSalaryData(): Promise<void> {
    const revisedData = await firstValueFrom(
      this.store.select(selectRevisedSalaryData).pipe(take(1))
    )

    const hasRevisedData =
      revisedData &&
      (revisedData.basic > 0 ||
        (revisedData.otherComponents && revisedData.otherComponents.length > 0))

    if (hasRevisedData) {
      this.setSalaryData(this.ensureIntegerAmounts(revisedData))
      this.recalculatePercentagesFromForm()
      this.dataLoaded = true
    } else {
      const originalData = await firstValueFrom(this.store.select(selectSalaryData).pipe(take(1)))

      if (originalData && originalData.basic > 0) {
        this.setSalaryData(this.ensureIntegerAmounts(originalData))
        this.recalculatePercentagesFromForm()
      } else {
        // ✅ Bilkul fresh — koi purana data hi nahi
        this.recalculatePercentagesFromForm()
        await this.calculateInitialValues()
      }
      this.dataLoaded = true
    }

    this.form.updateValueAndValidity()
    this.calculateSpecialAllowance()
  }

  //before 29 may 2026
  // private initializeComponentState(): void {
  //   this.earnings$ = this.store.select(selectSalaryComponents(CUSTOM_EARNINGS))
  //   this.deductions$ = this.store.select(selectSalaryComponents(CUSTOM_DEDUCTIONS))
  //   this.reimbursements$ = this.store.select(selectSalaryComponents(REIMBURSEMENTS))

  //   this.filteredEarnings$ = this.createFilteredObservable(this.earnings$)
  //   this.filteredDeductions$ = this.createFilteredObservable(this.deductions$)
  //   this.filteredReimbursements$ = this.createFilteredObservable(this.reimbursements$)

  //   // ✅ FIXED: Always use revised selectors for compliance and CTC
  //   // Because we always save to revisedSalaryData
  //   combineLatest([
  //     this.store.select(selectRevisedStatutoryCompliances),
  //     this.store.select(selectRevisedCtcValue)
  //   ])
  //     .pipe(
  //       take(1),
  //       tap(([complianceSettings, ctcValue]) => {
  //         if (!complianceSettings || ctcValue === undefined || ctcValue === null) {
  //           return
  //         }
  //         this.complianceSettings = complianceSettings
  //         this.ctcValue = ctcValue ?? 0
  //       })
  //     )
  //     .subscribe()
  // }

  //29 may 2026
  private initializeComponentState(): void {
    this.earnings$ = this.store.select(selectSalaryComponents(CUSTOM_EARNINGS))
    this.deductions$ = this.store.select(selectSalaryComponents(CUSTOM_DEDUCTIONS))
    this.reimbursements$ = this.store.select(selectSalaryComponents(REIMBURSEMENTS))

    this.filteredEarnings$ = this.createFilteredObservable(this.earnings$)
    this.filteredDeductions$ = this.createFilteredObservable(this.deductions$)
    this.filteredReimbursements$ = this.createFilteredObservable(this.reimbursements$)

    // ✅ Mode ke hisaab se sahi selectors use karo
    let complianceSelector: MemoizedSelector<object, StatutoryCompliances>
    let ctcSelector: MemoizedSelector<object, number>
    let salarySelector: MemoizedSelector<object, SalaryData>

    switch (this.currentMode) {
      case SalaryMode.CREATE:
        complianceSelector = selectStatutoryCompliances
        ctcSelector = selectCtcValue
        // salarySelector = selectSalaryData
        break
      case SalaryMode.REVISE:
      case SalaryMode.UPDATE:
        complianceSelector = selectRevisedStatutoryCompliances
        ctcSelector = selectRevisedCtcValue
        // salarySelector = selectRevisedSalaryData
        break
      default:
        throw new Error(`Unknown salary mode: ${this.currentMode}`)
    }

    combineLatest([
      this.store.select(complianceSelector),
      this.store.select(ctcSelector)
      // this.store.select(salarySelector)
    ])
      .pipe(
        take(1),
        tap(([complianceSettings, ctcValue]) => {
          if (!complianceSettings || ctcValue === undefined || ctcValue === null) {
            return
          }

          this.complianceSettings = complianceSettings
          this.ctcValue = ctcValue ?? 0
          this.gross = this.ctcValue // ✅ gross initialize karo

          // ✅ PF validator add karo compliance milne ke baad
          if (this.complianceSettings) {
            this.form.get('pf')?.addValidators(this.cappedAmountValidator.bind(this))
            this.form.get('pf')?.updateValueAndValidity({ emitEvent: false })
          }
          this.syncPfWithCompliance()
          // ✅ Agar existing data hai to load karo
          // if (salaryData?.basic > 0) {
          //   this.setSalaryData(this.ensureIntegerAmounts(salaryData))
          //   this.recalculatePercentagesFromForm()
          // } else {
          //   // ✅ Fresh load - initial values calculate karo
          //   this.recalculatePercentagesFromForm()
          //   this.calculateInitialValues()
          // }
          this.syncPfWithCompliance()
          this.setupFormValueChangeListeners()
          // this.form.updateValueAndValidity()
          // this.calculateSpecialAllowance()
        })
      )
      .subscribe()
  }

  private createFilteredObservable(
    source$: Observable<SalaryComponent[]>
  ): Observable<SalaryComponent[]> {
    return combineLatest([source$, this.searchTerm$]).pipe(
      map(([components, term]) => {
        if (!term?.trim()) {
          return components
        }
        const lowerCaseTerm = term.toLowerCase()
        return components.filter(component => component.name.toLowerCase().includes(lowerCaseTerm))
      })
    )
  }

  private ensureIntegerAmounts(salaryData: SalaryData): SalaryData {
    const processedData = { ...salaryData }
    processedData.basic = Math.ceil(processedData.basic ?? 0)
    processedData.hra = Math.ceil(processedData.hra ?? 0)
    processedData.pf = Math.ceil(processedData.pf ?? 0)

    if (processedData.otherComponents) {
      processedData.otherComponents = processedData.otherComponents.map(comp => ({
        ...comp,
        employerContribution: Math.ceil(comp.employerContribution ?? 0),
        employeeContribution: Math.ceil(comp.employeeContribution ?? 0)
      }))
    }
    return processedData
  }

  private setupFormValueChangeListeners(): void {
    // --- Basic Changes ---
    this.form
      .get('basic')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(newBasicValue => {
        if (!this.form.get('basic')?.valid || newBasicValue === null) return

        const ceiledNewBasic = Math.ceil(newBasicValue)
        const maxBasic = this.getMaxAllowedValue('basic')

        if (ceiledNewBasic > maxBasic) {
          this.form.get('basic')?.setValue(maxBasic, { emitEvent: false })
          this.updateBasicPercentage()
          this.calculateHraFromBasic()
        } else {
          if (ceiledNewBasic !== newBasicValue) {
            this.form.get('basic')?.setValue(ceiledNewBasic, { emitEvent: false })
          }
          this.updateBasicPercentage()
          this.calculateHraFromBasic()
        }
      })

    // --- HRA Changes ---
    this.form
      .get('hra')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(newHraValue => {
        if (!this.form.get('hra')?.valid || newHraValue === null) return

        const ceiledNewHra = Math.ceil(newHraValue)
        const maxHra = this.getMaxAllowedValue('hra')

        if (ceiledNewHra > maxHra) {
          this.form.get('hra')?.setValue(maxHra, { emitEvent: false })
          this.updateHraPercentage()
          this.calculateSpecialAllowance()
        } else {
          if (ceiledNewHra !== newHraValue) {
            this.form.get('hra')?.setValue(ceiledNewHra, { emitEvent: false })
          }
          this.updateHraPercentage()
          this.calculateSpecialAllowance()
        }
      })

    // --- PF Changes ---
    this.form
      .get('pf')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(newPfValue => {
        if (!this.form.get('pf')?.valid || newPfValue === null) return

        const ceiledNewPf = Math.ceil(newPfValue)
        if (ceiledNewPf !== newPfValue) {
          this.form.get('pf')?.setValue(ceiledNewPf, { emitEvent: false })
        }
        this.calculateSpecialAllowance()
      })

    // --- Other Components Changes ---
    this.addedComponentItems.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.calculateSpecialAllowance()
      })
  }

  // ✅ FIXED: Always save to revisedSalaryData, regardless of mode
  ngOnDestroy() {
    // Always dispatch to revisedSalaryData, even if form is invalid
    // because user made changes that should be preserved
    const rawValue = this.form.getRawValue()
    const finalPayload = this.ensureIntegerAmounts(rawValue)

    // ✅ ALWAYS save to revisedSalaryData
    this.store.dispatch(salaryRevisionActions.updateSalaryData({ salaryData: finalPayload }))
    if (this.currentMode === SalaryMode.CREATE) {
      this.store.dispatch(addSalaryData({ salaryData: finalPayload }))
    }

    localStorage.setItem(this.SALARY_CALC_CTC_KEY, String(this.ctcValue))
  }

  // ✅ NEW HELPER: Dispatch current form state to revisedSalaryData
  private dispatchToRevisedSalary(): void {
    const rawValue = this.form.getRawValue()
    const finalPayload = this.ensureIntegerAmounts(rawValue)
    this.store.dispatch(salaryRevisionActions.updateSalaryData({ salaryData: finalPayload }))
  }

  initializeForm() {
    this.form = this.fb.group(
      {
        basic: [0, [Validators.required, Validators.min(0)]],
        hra: [0, [Validators.required, Validators.min(0)]],
        pf: [0, [Validators.required, Validators.min(0)]],
        otherComponents: this.fb.array([]),
        specialAllowance: [{ value: 0, disabled: true }, Validators.required]
      }
      // { validators: this.ctcAllocationValidator() }
    )
    this.form.addValidators(this.ctcAllocationValidator())
    this.form.updateValueAndValidity()

    if (this.complianceSettings) {
      this.form.get('pf')?.addValidators(this.cappedAmountValidator.bind(this))
      this.form.get('pf')?.updateValueAndValidity()
    }
  }

  ctcAllocationValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!this.form || !this.ctcValue || !control) {
        return null
      }

      const formGroup = control as FormGroup
      const basic = formGroup.get('basic')?.value ?? 0
      const hra = formGroup.get('hra')?.value ?? 0
      const pf = formGroup.get('pf')?.value ?? 0
      const otherComponentsSum = this.getOtherComponentsSumInCtc()

      const totalAllocated = Number(basic) + Number(hra) + Number(pf) + Number(otherComponentsSum)

      return totalAllocated > this.ctcValue ? { ctcExceeded: true } : null
    }
  }

  cappedAmountValidator(control: AbstractControl): ValidationErrors | null {
    if (!this.complianceSettings) return null

    const maxAmount = this.complianceSettings.employerCapping
      ? Math.floor(this.annualPfCap)
      : Number.MAX_SAFE_INTEGER

    if (control.value !== null) {
      const controlValue = Math.ceil(control.value)
      if (controlValue > maxAmount) {
        control.setValue(maxAmount, { emitEvent: false })
      } else if (controlValue < 0) {
        return { negativeValue: true }
      } else if (control.value !== controlValue) {
        control.setValue(controlValue, { emitEvent: false })
      }
    }
    return null
  }

  getErrorMessage(controlName: string): string | null {
    const control = this.form.get(controlName)
    if (control && control.touched && control.invalid) {
      if (control.hasError('required')) {
        return 'This field is required'
      }
      if (control.hasError('min')) {
        return 'Value cannot be negative'
      }
      if (control.hasError('negativeValue')) {
        return 'Value cannot be negative'
      }
    }
    if (controlName === 'form' && this.form.hasError('ctcExceeded')) {
      const displayCTC = Math.floor(this.ctcValue)
      return `Total allocated salary components exceed the total CTC (${displayCTC}).`
    }
    return null
  }

  createOtherComponentGroup(componentData?: ExtendedSalaryComponent): FormGroup {
    return this.fb.group({
      id: [componentData?.id],
      belongsTo: [componentData?.belongsTo, Validators.required],
      name: [componentData?.name, Validators.required],
      description: [componentData?.description],
      componentType: [componentData?.componentType, Validators.required],
      info: this.fb.group({
        deductionPlan: [componentData?.info?.deductionPlan],
        payType: [componentData?.info?.payType],
        calculationType: [componentData?.info?.calculationType],
        paymentFrequency: [componentData?.info?.paymentFrequency],
        taxDeductionPreference: [componentData?.info?.taxDeductionPreference],
        taxable: [componentData?.info?.taxable ?? false],
        isInCTC: [componentData?.info?.isInCTC ?? false],
        isInFBP: [componentData?.info?.isInFBP ?? false],
        proRataBasis: [componentData?.info?.proRataBasis ?? false],
        EPFContribution: [componentData?.info?.EPFContribution ?? false],
        ESIContribution: [componentData?.info?.ESIContribution ?? false],
        optional: [componentData?.info?.optional ?? false],
        employerContribution: [componentData?.info?.employerContribution ?? 3.25],
        employeeContribution: [componentData?.info?.employeeContribution ?? 0.75]
      }),

      employerContribution: [
        Math.ceil(componentData?.employerContribution ?? 0),
        [Validators.required, Validators.min(0)]
      ],
      employeeContribution: [
        Math.ceil(componentData?.employeeContribution ?? 0),
        [Validators.required, Validators.min(0)]
      ]
    })
  }

  setSalaryData(salaryData: SalaryData): void {
    const otherComponentsArray = this.form.get('otherComponents') as FormArray<FormGroup>
    otherComponentsArray.clear({ emitEvent: false })

    if (salaryData.otherComponents && Array.isArray(salaryData.otherComponents)) {
      salaryData.otherComponents.forEach((component: ExtendedSalaryComponent) => {
        const componentGroup = this.createOtherComponentGroup(component)
        otherComponentsArray.push(componentGroup, { emitEvent: false })
      })
    }

    const { otherComponents: _, specialAllowance: _specialAllowance, ...topLevelData } = salaryData
    const { basic, hra, pf, ...restTopLevelData } = topLevelData

    const integerTopLevelData = {
      basic: Math.ceil(topLevelData.basic ?? 0),
      hra: Math.ceil(topLevelData.hra ?? 0),
      pf: Math.ceil(topLevelData.pf ?? 0),
      ...restTopLevelData
    }

    this.form.patchValue(integerTopLevelData, { emitEvent: false })
    this.syncPfWithCompliance()
    if (basic > 0) {
      this.updateBasicPercentage()
      this.updateHraPercentage()
      // this.calculateHraFromBasic()
    } else {
      this.calculateInitialValues()
    }

    this.form.updateValueAndValidity({ emitEvent: false })
    this.calculateSpecialAllowance()
  }

  //before 27 july
  // private recalculatePercentagesFromForm(): void {
  //   const basic = this.form.get('basic')?.value || 0
  //   const hra = this.form.get('hra')?.value || 0

  //   if (this.ctcValue > 0 && this.ctcValue === this.currentCtcValue) {
  //     this.gross = this.gross > 0 ? this.gross : this.ctcValue
  //     this.basicPercentage = Number(((basic / this.gross) * 100).toFixed(2))
  //   } else if (this.ctcValue > 0 && this.ctcValue !== this.currentCtcValue) {
  //     this.basicPercentage = 50
  //     this.hraPercentage = 40
  //   } else {
  //     this.basicPercentage = 0
  //   }
  // }

  //7 aug 2026 Salary Revision Calculation Uses Previous Values Instead of Updated Data AH-762 GPS
  private recalculatePercentagesFromForm(): void {
    const basic = this.form.get('basic')?.value || 0
    const hra = this.form.get('hra')?.value || 0

    if (this.ctcValue > 0 && this.ctcValue === this.currentCtcValue) {
      // CTC same hai — percentage seedha current basic/gross se nikalo
      this.gross = this.gross > 0 ? this.gross : this.ctcValue
      this.basicPercentage =
        basic > 0 ? Number(((basic / this.gross) * 100).toFixed(2)) : this.basicPercentage
      this.hraPercentage =
        hra > 0 && basic > 0 ? Number(((hra / basic) * 100).toFixed(2)) : this.hraPercentage
    }
    // else if (this.ctcValue > 0 && this.ctcValue !== this.currentCtcValue) {
    //   // ✅ CTC revise/update hua hai
    //   // Purana % nikalo OLD ctc (currentCtcValue) ke against — hardcoded 50/40 mat karo
    //   const oldGross = this.currentCtcValue > 0 ? this.currentCtcValue : this.ctcValue

    //   this.basicPercentage =
    //     basic > 0 && oldGross > 0
    //       ? Number(((basic / oldGross) * 100).toFixed(2))
    //       : this.basicPercentage || 50

    //   this.hraPercentage =
    //     basic > 0 && hra > 0 ? Number(((hra / basic) * 100).toFixed(2)) : this.hraPercentage || 40

    //   // ✅ Naya CTC hi ab baseline ban jayega
    //   this.currentCtcValue = this.ctcValue

    //   // ✅ Purane % ke hisaab se naya basic/hra naye gross par calculate karo
    //   this.recomputeAmountsFromPercentages()

    //   // ✅ Naye gross ke against ESI eligibility check karo
    //   this.enforceEsiEligibility()
    // }
    else if (this.ctcValue > 0 && this.ctcValue !== this.currentCtcValue) {
      const oldGross = this.currentCtcValue > 0 ? this.currentCtcValue : this.ctcValue

      this.basicPercentage =
        basic > 0 && oldGross > 0
          ? Number(((basic / oldGross) * 100).toFixed(2))
          : this.basicPercentage || 50

      this.hraPercentage =
        basic > 0 && hra > 0 ? Number(((hra / basic) * 100).toFixed(2)) : this.hraPercentage || 40

      // Pehle old CTC se comparison karein; baseline abhi update na karein
      const isCtcReduced = this.ctcValue < this.currentCtcValue

      // Existing components recalculate karein
      this.recalculateExistingComponents()

      // Basic aur HRA recalculate karein
      this.recomputeAmountsFromPercentages()

      // CTC reduce hua hai to deduction eligibility check karein
      if (isCtcReduced) {
        this.removeDeductionsIfCtcExceeded()
      }

      // ESI eligibility check karein
      this.getGross()
      this.enforceEsiEligibility()

      // New CTC baseline
      this.currentCtcValue = this.ctcValue

      // Existing deductions/contributions recalculate karein
      this.recalculateExistingComponents()

      // Basic aur HRA recalculate karein
      this.recomputeAmountsFromPercentages()

      // ESI eligibility dobara check karein
      this.enforceEsiEligibility()
    } else {
      this.basicPercentage = 0
    }
  }

  //27 july 2026 gps
  // private recalculatePercentagesFromForm(): void {
  //   const basic = this.form.get('basic')?.value || 0
  //   const hra = this.form.get('hra')?.value || 0

  //   if (this.gross > 0) {
  //     // Percentage hamesha actual data se derive karo, hardcoded reset mat karo
  //     this.basicPercentage = Number(((basic / this.gross) * 100).toFixed(2))
  //     this.hraPercentage = basic > 0 ? Number(((hra / basic) * 100).toFixed(2)) : 0
  //   } else {
  //     this.basicPercentage = 50
  //   }
  // }

  async calculateInitialValues() {
    await this.getGross()
    if (this.gross <= 0) {
      this.form.patchValue({ basic: 0, hra: 0, pf: 0 }, { emitEvent: false })
      this.calculateSpecialAllowance()
      return
    }
    const basic = Math.ceil((this.gross * this.basicPercentage) / 100)
    const hra = Math.ceil((basic * this.hraPercentage) / 100)
    let pf = 0

    if (this.complianceSettings?.pf) {
      pf = Math.ceil(basic * 0.12)
      if (this.complianceSettings.employerCapping) {
        pf = Math.min(pf, 21600)
      }
    }

    this.form.patchValue({ basic, hra, pf }, { emitEvent: false })
    this.syncPfWithCompliance()
    this.calculateSpecialAllowance()
  }

  getMaxAllowedValue(fieldName: 'basic' | 'hra' | 'pf'): number {
    const currentBasic = this.form.get('basic')?.value ?? 0
    const currentHra = this.form.get('hra')?.value ?? 0
    const currentPf = this.form.get('pf')?.value ?? 0
    const otherComponentsSum = this.getOtherComponentsSumInCtc()

    let fixedSum = 0
    switch (fieldName) {
      case 'basic':
        fixedSum = Number(currentHra) + Number(currentPf) + otherComponentsSum
        break
      case 'hra':
        fixedSum = Number(currentBasic) + Number(currentPf) + otherComponentsSum
        break
      case 'pf':
        fixedSum = Number(currentBasic) + Number(currentHra) + otherComponentsSum
        break
    }

    const maxValue = Math.floor(this.ctcValue - fixedSum)
    return Math.max(0, maxValue)
  }

  updateBasicPercentage() {
    const basic = this.form.get('basic')?.value || 0
    if (this.ctcValue > 0) {
      this.gross = this.gross > 0 ? this.gross : this.ctcValue
      this.basicPercentage = Number(((basic / this.gross) * 100).toFixed(2))
    } else {
      this.basicPercentage = 0
    }
  }

  updateHraPercentage() {
    const hra = this.form.get('hra')?.value || 0
    const basic = this.form.get('basic')?.value || 0
    if (basic > 0) {
      this.hraPercentage = Number(((hra / basic) * 100).toFixed(2))
    } else {
      this.hraPercentage = 0
    }
  }

  updateBasicAmount(event: Event) {
    const inputElement = event.target as HTMLInputElement
    const percentage = inputElement.valueAsNumber
    if (isNaN(percentage) || percentage < 0) return

    this.basicPercentage = percentage
    const rawBasicAmount = (this.gross * this.basicPercentage) / 100
    const basicCeiled = Math.ceil(rawBasicAmount)

    const maxBasic = this.getMaxAllowedValue('basic')
    if (basicCeiled > maxBasic) {
      this.form.get('basic')?.setValue(maxBasic)
      if (this.ctcValue > 0) {
        this.basicPercentage = Number(((maxBasic / this.gross) * 100).toFixed(2))
        inputElement.value = this.basicPercentage.toFixed(3)
      }
    } else {
      this.form.get('basic')?.setValue(basicCeiled)
    }
  }

  updateHraAmount(event: Event) {
    const inputElement = event.target as HTMLInputElement
    const percentage = inputElement.valueAsNumber
    if (isNaN(percentage) || percentage < 0) return

    this.hraPercentage = percentage
    const basic = this.form.get('basic')?.value || 0
    const rawHraAmount = (basic * this.hraPercentage) / 100
    const hraCeiled = Math.ceil(rawHraAmount)

    const maxHra = this.getMaxAllowedValue('hra')

    if (hraCeiled > maxHra) {
      this.form.get('hra')?.setValue(maxHra)
      if (basic > 0) {
        this.hraPercentage = Number(((maxHra / basic) * 100).toFixed(2))
        inputElement.value = this.hraPercentage.toFixed(3)
      }
    } else {
      this.form.get('hra')?.setValue(hraCeiled)
    }
  }

  calculateHraFromBasic() {
    const basic = this.form.get('basic')?.value || 0
    const rawHraAmount = (basic * this.hraPercentage) / 100
    const hraCeiled = Math.ceil(rawHraAmount)

    const maxHra = this.getMaxAllowedValue('hra')

    if (hraCeiled > maxHra) {
      this.form.get('hra')?.setValue(maxHra, { emitEvent: false })
      this.updateHraPercentage()
      this.calculateSpecialAllowance()
    } else {
      this.form.get('hra')?.setValue(hraCeiled, { emitEvent: false })
      this.calculateSpecialAllowance()
    }
  }

  private getOtherComponentsSumInCtc(): number {
    if (!this.form) return 0
    return this.addedComponentItems.controls
      .filter(ctrl => ctrl.get('info')?.get('isInCTC')?.value)
      .reduce((sum: number, item: AbstractControl) => {
        const componentType = item.get('componentType')?.value
        const employerContribution = Number(item.get('employerContribution')?.value || 0)
        const employeeContribution = Number(item.get('employeeContribution')?.value || 0)

        if (componentType === 'customDeductions') {
          return sum + employerContribution + employeeContribution
        }
        return sum + employerContribution
      }, 0)
  }

  async calculateSpecialAllowance() {
    await this.getGross()
    this.enforceEsiEligibility()
    const basic = Number(this.form.get('basic')?.value || 0)
    const hra = Number(this.form.get('hra')?.value || 0)
    const pf = Number(this.form.get('pf')?.value || 0)
    if (basic === 0) {
      this.basicPercentage = 50
    }

    const otherCtc = this.getOtherComponentsSumInCtc()
    const specialAllowance = Math.max(0, Math.floor(this.ctcValue - (basic + hra + pf + otherCtc)))

    this.form.patchValue({ specialAllowance }, { emitEvent: false })
    this.form.updateValueAndValidity({ emitEvent: false })

    this.removeDeductionsBasedOnTakeHome()
  }

  openModel(modal: unknown, componentName = '') {
    this.addCompError = null
    const modalRef = this.modalService.open(modal, {
      scrollable: false,
      centered: true,
      size: 'md'
    })
    if (modalRef.componentInstance) modalRef.componentInstance.salaryComponentName = componentName
  }

  onSearchChange(): void {
    this.searchTerm$.next(this.searchTerm)
  }

  onClose() {
    this.selectedComponentItem = null
    this.employerContribution = 0
    this.employeeContribution = 0
    this.addCompError = null
    this.isEditing = false
    this.searchTerm = ''
    this.searchTerm$.next('')
    this.modalService.dismissAll()
  }

  get addedComponentItems(): FormArray {
    if (!this.form) {
      return this.fb.array([])
    }
    return this.form.get('otherComponents') as FormArray
  }

  get notInCtcComponents() {
    if (!this.form) return []

    return this.addedComponentItems.controls
      .map((control, index) => ({
        value: control.value,
        originalIndex: index
      }))
      .filter(item => !item.value.info?.isInCTC)
  }

  get inCtcComponents() {
    if (!this.form) return []
    return this.addedComponentItems.controls.filter(item => item.get('info.isInCTC')?.value)
  }

  isComponentItemAdded(item: SalaryComponent): boolean {
    return this.addedComponentItems.value.some(
      (added: { id?: string; name?: string }) => added.id === item.id || added.name === item.name
    )
  }

  onRadioChange(item: SalaryComponent) {
    this.addCompError = null
    this.selectedComponentItem = item
    this.isEditing = false
    this.employerContribution = 0
    this.employeeContribution = 0
  }

  startEditing(item: SalaryComponent): void {
    this.isEditing = true
    this.selectedComponentItem = item
    this.employerContribution = this.getAddedContributionOfEmployer(item)
    this.employeeContribution = this.getAddedContributionOfEmployee(item)
    this.addCompError = null
  }

  addComponentItem(): void {
    this.addCompError = null
    if (!this.selectedComponentItem) {
      this.addCompError = 'Please select a component to add.'
      return
    }

    const isInCTC = this.selectedComponentItem.info?.isInCTC ?? false
    const calculationType = this.selectedComponentItem.info?.calculationType || 'fixed'
    const isESI = this.selectedComponentItem.info?.ESIContribution ?? false

    let employerContributionCeiled = 0
    let employeeContributionCeiled = 0

    if (calculationType !== 'percentage') {
      employerContributionCeiled = Math.ceil(this.employerContribution ?? 0)
      employeeContributionCeiled = Math.ceil(this.employeeContribution ?? 0)
    } else if (calculationType === 'percentage' && isInCTC) {
      const employerRate = (this.selectedComponentItem.info?.employerContribution ?? 3.25) / 100
      const employeeRate = (this.selectedComponentItem.info?.employeeContribution ?? 0.75) / 100
      this.selectedComponentItem.info = {
        ...this.selectedComponentItem.info,
        employerContribution: this.selectedComponentItem.info?.employerContribution ?? 3.25,
        employeeContribution: this.selectedComponentItem.info?.employeeContribution ?? 0.75
      }

      let existingCtcDeductions = this.getOtherComponentsSumInCtc()
      if (this.isEditing) {
        existingCtcDeductions -= this.getAddedContributionOfEmployer(this.selectedComponentItem)
      }

      const availableCtc = this.ctcValue
      const annualGross = availableCtc / (1 + employerRate)
      const monthlyGross = annualGross / 12

      employerContributionCeiled = Math.ceil(monthlyGross * employerRate * 12)
      employeeContributionCeiled = Math.ceil(monthlyGross * employeeRate * 12)
    } else if (calculationType === 'percentage' && !isInCTC) {
      this.getGross()
      const monthlyGross = this.gross / 12

      const employerRate = (this.selectedComponentItem.info?.employerContribution ?? 3.25) / 100
      const employeeRate = (this.selectedComponentItem.info?.employeeContribution ?? 0.75) / 100

      if (isESI) {
        const esiLimit = (this.complianceSettings as any)?.esiLimit ?? 21000
        if (monthlyGross <= esiLimit) {
          employerContributionCeiled = Math.ceil(monthlyGross * employerRate * 12)
          employeeContributionCeiled = Math.ceil(monthlyGross * employeeRate * 12)
        } else {
          employerContributionCeiled = 0
          employeeContributionCeiled = 0
        }
      } else {
        employerContributionCeiled = Math.ceil(monthlyGross * employerRate * 12)
        employeeContributionCeiled = Math.ceil(monthlyGross * employeeRate * 12)
      }
    }

    if (employerContributionCeiled < 0 || employeeContributionCeiled < 0) {
      this.addCompError = 'Contribution amounts cannot be negative.'
      return
    }

    let currentOtherCtcSum = this.getOtherComponentsSumInCtc()
    if (this.isEditing) {
      currentOtherCtcSum -= this.getAddedContributionOfEmployer(this.selectedComponentItem)
    }

    const newCtcContribution = isInCTC ? employerContributionCeiled : 0
    const basic = this.form.get('basic')?.value || 0
    const hra = this.form.get('hra')?.value || 0
    const pf = this.form.get('pf')?.value || 0

    const potentialTotal =
      Number(basic) + Number(hra) + Number(pf) + currentOtherCtcSum + newCtcContribution

    if (potentialTotal > this.ctcValue) {
      const over = potentialTotal - this.ctcValue
      this.addCompError = `CTC exceeded by ₹${over.toFixed(0)}`
      return
    }

    if (this.isEditing) {
      const idx = this.addedComponentItems.controls.findIndex(
        ctrl => ctrl.value.id === this.selectedComponentItem!.id
      )
      if (idx > -1) this.addedComponentItems.removeAt(idx)
    }

    const newItemData: ExtendedSalaryComponent = {
      ...this.selectedComponentItem,
      info: {
        ...this.selectedComponentItem.info,
        employerContribution: this.selectedComponentItem.info?.employerContribution ?? 3.25,
        employeeContribution: this.selectedComponentItem.info?.employeeContribution ?? 0.75
      },
      employerContribution: employerContributionCeiled,
      employeeContribution: employeeContributionCeiled
    }
    this.addedComponentItems.push(this.createOtherComponentGroup(newItemData))

    if (isInCTC && this.selectedComponentItem.componentType === 'customDeductions') {
      this.getGross()
      const newBasic = Math.ceil((this.gross * this.basicPercentage) / 100)
      const newHra = Math.ceil((newBasic * this.hraPercentage) / 100)
      this.form.patchValue({ basic: newBasic, hra: newHra }, { emitEvent: false })
    }

    this.calculateSpecialAllowance()

    // ✅ FIXED: Immediately dispatch to store after adding
    this.dispatchToRevisedSalary()

    this.onClose()
  }

  // ✅ FIXED: Remove component and immediately update store
  async removeComponentItem(index: number) {
    if (index < 0 || index >= this.addedComponentItems.length) return

    const item = this.addedComponentItems.at(index)
    const value = item?.value
    const isEsi =
      value?.info?.ESIContribution === true || (value?.name || '').toLowerCase().includes('esi')

    this.addedComponentItems.removeAt(index)

    // ✅ FIXED: Immediately dispatch to store after removing
    this.dispatchToRevisedSalary()

    this.getGross()

    if (isEsi) {
      this.esiEmployerContribution = 0
      this.esiEmployeeContribution = 0
      this.gross = this.ctcValue

      await this.calculateInitialValues()
      await this.updateBasicPercentage()
      await this.updateHraPercentage()
    }

    await this.calculateSpecialAllowance()
    await this.form.updateValueAndValidity()
  }

  removeComponentItemFromModal(item: SalaryComponent): void {
    const index = this.addedComponentItems.controls.findIndex(
      ctrl => ctrl.value.id === item.id || ctrl.value.name === item.name
    )

    if (index > -1) {
      this.removeComponentItem(index)
    }

    if (this.selectedComponentItem?.id === item.id) {
      this.selectedComponentItem = null
      this.isEditing = false
      this.employerContribution = 0
      this.employeeContribution = 0
    }
  }

  isContractor(employee: any): boolean {
    return employee?.userRoles?.some((r: UserRole) => r?.role?.name === 'Contractor') || false
  }

  private calculateEmployerEsi(monthlyGross: number, manualValue?: number): number {
    if (manualValue !== undefined) return manualValue
    const esiRate = (this.complianceSettings as any)?.esiEmployerRate ?? 3.25
    const esiLimit = (this.complianceSettings as any)?.esiLimit ?? 21000
    if (monthlyGross <= esiLimit) {
      return Math.floor(monthlyGross * (esiRate / 100))
    }
    return 0
  }

  private calculateEmployeeEsi(monthlyGross: number, manualValue?: number): number {
    if (manualValue !== undefined) return manualValue
    const esiRate = (this.complianceSettings as any)?.esiEmployeeRate ?? 0.75
    const esiLimit = (this.complianceSettings as any)?.esiLimit ?? 21000
    if (monthlyGross <= esiLimit) {
      return Math.floor(monthlyGross * (esiRate / 100))
    }
    return 0
  }

  getAddedContributionOfEmployer(item: SalaryComponent): number {
    const found = this.addedComponentItems.controls.find(
      ctrl => ctrl.value.id === item.id || ctrl.value.name === item.name
    )
    return Number(found?.get('employerContribution')?.value || 0)
  }

  getAddedContributionOfEmployee(item: SalaryComponent): number {
    const found = this.addedComponentItems.controls.find(
      ctrl => ctrl.value.id === item.id || ctrl.value.name === item.name
    )
    return Number(found?.get('employeeContribution')?.value || 0)
  }

  private getMonthlyCtc(): number {
    return this.ctcValue / 12
  }

  private calculateEsiFromGross(
    grossMonthly: number,
    enteredEmployer?: number,
    enteredEmployee?: number
  ) {
    if (enteredEmployer !== undefined && enteredEmployee !== undefined) {
      return { employer: enteredEmployer, employee: enteredEmployee }
    }
    if (grossMonthly > 21000) {
      return { employer: 0, employee: 0 }
    }
    return {
      employer: Math.floor(grossMonthly * 0.0325),
      employee: Math.floor(grossMonthly * 0.0075)
    }
  }

  get earningComponents() {
    if (!this.form) return []
    return this.inCtcComponents.filter(
      (x: any) => x.value.componentType !== 'customDeductions' && !x.value.info?.ESIContribution
    )
  }

  get deductionComponents() {
    if (!this.form) return []
    return this.addedComponentItems.controls
      .map((control, index) => ({
        value: control.value,
        originalIndex: index
      }))
      .filter((x: any) => x.value.info?.isInCTC && x.value.componentType === 'customDeductions')
  }

  get monthlyTakeHome(): number {
    if (!this.form) return 0
    const gross = this.calculateGrossEarnings()
    const reimbursements = this.calculateReimbursements()
    const deductions = this.calculateDeductions()
    const annualTakeHome = gross + reimbursements - deductions
    return Math.floor(annualTakeHome / 12)
  }

  private calculateGrossEarnings(): number {
    const basic = Number(this.form.get('basic')?.value || 0)
    const hra = Number(this.form.get('hra')?.value || 0)
    const specialAllowance = Number(this.form.get('specialAllowance')?.value || 0)

    let dynamicEarnings = 0
    this.addedComponentItems.controls.forEach(ctrl => {
      const type = ctrl.get('componentType')?.value?.toLowerCase()
      const isInCTC = ctrl.get('info')?.get('isInCTC')?.value
      const value = Number(ctrl.get('employerContribution')?.value || 0)

      if (type === 'customearnings') {
        dynamicEarnings += value
      }
      if (type === 'customdeductions' && isInCTC) {
        const value = Number(ctrl.get('employeeContribution')?.value || 0)
        dynamicEarnings += value
      }
    })

    return basic + hra + specialAllowance + dynamicEarnings
  }

  private calculateReimbursements(): number {
    return this.addedComponentItems.controls.reduce((sum, ctrl) => {
      const type = ctrl.get('componentType')?.value?.toLowerCase()
      const isFBP = ctrl.get('isInFBP')?.value
      const value = Number(ctrl.get('amount')?.value || 0)

      if (type === 'reimbursements' || isFBP === true) {
        return sum + value
      }
      return sum
    }, 0)
  }

  private calculateDeductions(): number {
    const pf = Number(this.form.get('pf')?.value || 0)
    let total = pf

    this.addedComponentItems.controls.forEach(ctrl => {
      const type = (ctrl.get('componentType')?.value || '').toLowerCase()
      const isESI = ctrl.get('info')?.get('ESIContribution')?.value
      const employeeContrib = Number(ctrl.get('employeeContribution')?.value || 0)

      if (type === 'customdeductions' || isESI) {
        total += employeeContrib
      }
    })

    return total
  }

  private monthlyTakeHomes() {
    return this.monthlyTakeHome
  }

  private getGross(): number {
    if (!this.form) {
      this.gross = this.ctcValue
      return this.gross
    }
    const controls = this.addedComponentItems?.controls ?? []
    let employerComponents = 0

    employerComponents += controls.reduce((sum, ctrl) => {
      const type = ctrl.get('componentType')?.value?.toLowerCase()
      const isInCTC = ctrl.get('info')?.get('isInCTC')?.value

      if (type !== 'customdeductions' || !isInCTC) {
        return sum
      }
      return sum + Number(ctrl.get('employerContribution')?.value || 0)
    }, 0)

    const pf = Number(this.form.get('pf')?.value || 0)
    employerComponents += pf

    if (this.currentCtcValue === 0) {
      this.currentCtcValue = this.ctcValue
    }
    this.gross = this.ctcValue - employerComponents
    return this.gross
  }

  private calculateGrossFromCtc(ctc: number): number {
    const controls = this.getEffectiveComponents()
    let deductionFromCTC = 0

    for (const ctrl of controls) {
      const value = ctrl.value
      const isInCTC = value?.info?.isInCTC
      const componentType = value?.info?.componentType?.toLowerCase()
      const amount = Number(value?.employerContribution || 0)

      if (isInCTC && componentType === 'customdeduction') {
        deductionFromCTC += amount
      }
    }

    const gross = Number(this.ctcValue || 0) - deductionFromCTC
    this.gross = gross
    return gross
  }

  private getEffectiveComponents(): AbstractControl[] {
    if (this.addedComponentItems?.controls?.length) {
      return this.addedComponentItems.controls
    }
    if (this.selectedComponentItem) {
      return [this.createTempControl(this.selectedComponentItem)]
    }
    return []
  }

  private createTempControl(item: any): AbstractControl {
    return this.fb.group({
      id: item.id,
      name: item.name,
      componentType: item.componentType,
      employerContribution: item.employerContribution || 0,
      employeeContribution: item.employeeContribution || 0,
      info: item.info || {}
    })
  }

  private getInCtcComponents() {
    const controls = this.getEffectiveComponents()
    return controls.filter(ctrl => ctrl.value?.info?.isInCTC === true)
  }

  private syncPfWithCompliance(): void {
    const pfControl = this.form.get('pf')
    if (!pfControl) return

    if (!this.complianceSettings?.pf) {
      // PF compliance OFF hai -> PF hamesha 0, aur field disable
      if (pfControl.value !== 0) {
        pfControl.setValue(0, { emitEvent: false })
      }
      pfControl.disable({ emitEvent: false })
    } else {
      // PF compliance ON hai -> field enable karo agar disabled tha
      if (pfControl.disabled) {
        pfControl.enable({ emitEvent: false })
      }
    }
  }

  // ✅ NEW: purane basicPercentage/hraPercentage ke hisaab se naye gross par
  // basic/hra amount recalculate karo (CTC exceed na ho, isliye max cap bhi lagao)
  private recomputeAmountsFromPercentages(): void {
    this.getGross()
    if (this.gross <= 0) {
      this.form.patchValue({ basic: 0, hra: 0 }, { emitEvent: false })
      return
    }

    let basic = Math.ceil((this.gross * this.basicPercentage) / 100)
    const maxBasic = this.getMaxAllowedValue('basic')
    basic = Math.min(basic, maxBasic)
    this.form.patchValue({ basic }, { emitEvent: false })

    let hra = Math.ceil((basic * this.hraPercentage) / 100)
    const maxHra = this.getMaxAllowedValue('hra')
    hra = Math.min(hra, maxHra)
    this.form.patchValue({ hra }, { emitEvent: false })
  }

  // ✅ NEW: agar naye gross par ESI eligibility limit cross ho jaye,
  // to already-added ESI component ko hata do
  private enforceEsiEligibility(): void {
    if (!this.form) return

    const esiLimit = (this.complianceSettings as any)?.esiLimit ?? 21000
    const monthlyGross = this.gross / 12

    const esiIndex = this.addedComponentItems.controls.findIndex(
      ctrl => ctrl.get('info')?.get('ESIContribution')?.value === true
    )

    if (esiIndex > -1 && monthlyGross > esiLimit) {
      this.addedComponentItems.removeAt(esiIndex)
      this.esiEmployerContribution = 0
      this.esiEmployeeContribution = 0
    }
  }

  private recalculateExistingComponents(): void {
    if (!this.form) return

    const components = this.addedComponentItems.controls

    const esiLimit = (this.complianceSettings as any)?.esiLimit ?? 21000

    // Pehle PF compliance sync karein
    this.syncPfWithCompliance()

    // Current components mein percentage-based amounts recalculate karein
    components.forEach(control => {
      const info = control.get('info')
      if (!info) return

      const calculationType = info.get('calculationType')?.value
      const isInCTC = info.get('isInCTC')?.value === true
      const isESI = info.get('ESIContribution')?.value === true

      // Fixed components ko modify nahi karna
      if (calculationType !== 'percentage') return

      const employerRate = Number(info.get('employerContribution')?.value ?? 3.25) / 100

      const employeeRate = Number(info.get('employeeContribution')?.value ?? 0.75) / 100

      // CTC mein included component ke liye employer contribution
      // calculate karne se pehle available gross determine karein
      let monthlyGross = this.gross / 12

      if (isInCTC) {
        const currentEmployer = Number(control.get('employerContribution')?.value ?? 0)

        // Current component ko temporarily gross calculation se exclude karein
        const otherEmployerDeductions = components.reduce((sum, other) => {
          if (other === control) return sum

          if (
            other.get('componentType')?.value === 'customDeductions' &&
            other.get('info.isInCTC')?.value
          ) {
            return sum + Number(other.get('employerContribution')?.value ?? 0)
          }

          return sum
        }, 0)

        const pf = Number(this.form.get('pf')?.value ?? 0)

        const availableGross = this.ctcValue - otherEmployerDeductions - pf

        // Percentage contribution CTC ka hissa hai
        monthlyGross = availableGross > 0 ? availableGross / (12 * (1 + employerRate)) : 0

        // currentEmployer intentionally not used as a fixed value;
        // amount is calculated afresh from the new CTC
        void currentEmployer
      }

      // ESI eligibility
      if (isESI && monthlyGross > esiLimit) {
        control.get('employerContribution')?.setValue(0, { emitEvent: false })
        control.get('employeeContribution')?.setValue(0, { emitEvent: false })
        return
      }

      const employerAmount = Math.ceil(monthlyGross * employerRate * 12)
      const employeeAmount = Math.ceil(monthlyGross * employeeRate * 12)

      control.get('employerContribution')?.setValue(employerAmount, { emitEvent: false })

      control.get('employeeContribution')?.setValue(employeeAmount, { emitEvent: false })
    })

    // Contributions change hone ke baad gross dobara calculate karein
    this.getGross()
  }

  //ctc lessthan current ctc and the deductions not applied then remove the deductions
  private removeDeductionsIfCtcExceeded(): void {
    if (!this.form || this.ctcValue >= this.currentCtcValue) {
      return
    }

    const getAllocatedTotal = (): number => {
      const basic = Number(this.form.get('basic')?.value ?? 0)
      const hra = Number(this.form.get('hra')?.value ?? 0)
      const pf = Number(this.form.get('pf')?.value ?? 0)

      const deductions = this.addedComponentItems.controls
        .filter(
          control =>
            control.get('componentType')?.value === 'customDeductions' &&
            control.get('info.isInCTC')?.value === true
        )
        .reduce((sum, control) => sum + Number(control.get('employerContribution')?.value ?? 0), 0)

      return basic + hra + pf + deductions
    }

    // Jab tak total CTC ke andar nahi aa jata, deductions remove karo
    for (
      let i = this.addedComponentItems.length - 1;
      i >= 0 && getAllocatedTotal() > this.ctcValue;
      i--
    ) {
      const control = this.addedComponentItems.at(i)

      if (
        control.get('componentType')?.value === 'customDeductions' &&
        control.get('info.isInCTC')?.value === true
      ) {
        this.addedComponentItems.removeAt(i, { emitEvent: false })

        // Har removal ke baad allocation dobara calculate karo
        this.getGross()
        this.recomputeAmountsFromPercentages()
      }
    }

    this.calculateSpecialAllowance()
    this.form.updateValueAndValidity({ emitEvent: false })
    this.dispatchToRevisedSalary()
  }

  private removeDeductionsBasedOnTakeHome(): void {
    if (!this.form) return

    const takeHome = this.monthlyTakeHome

    // Take-home 0 ya 15000 ya usse zyada ho
    if (!(takeHome === 0 || this.currentCtcValue >= 180000)) {
      return
    }

    let removed = false

    for (let i = this.addedComponentItems.length - 1; i >= 0; i--) {
      const control = this.addedComponentItems.at(i)

      const isDeduction = control.get('componentType')?.value === 'customDeductions'

      const isInCTC = control.get('info.isInCTC')?.value === true

      if (isDeduction && isInCTC) {
        this.addedComponentItems.removeAt(i, { emitEvent: false })
        removed = true
      }
    }

    if (!removed) return

    // Salary dobara calculate karein
    this.getGross()
    this.recomputeAmountsFromPercentages()
    this.calculateSpecialAllowance()

    // Updated salary store mein save karein
    this.dispatchToRevisedSalary()
  }
  //ctc lessthan current ctc and the deductions not applied then remove the deductions --end
}
